import React, { useEffect, useState, useCallback } from "react";
import { Spinner, Badge, Button, Alert } from "react-bootstrap";
import { FaEnvelope, FaSync, FaRedo } from "react-icons/fa";
import { apiFetch } from "./utils/auth";

const API_URL = process.env.REACT_APP_API_URL;

function fmtMoney(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(v));
}
function fmtWhen(v) {
  if (!v) return "";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}
function ackBadge(status) {
  return status === "Sent" ? "success" : status === "Failed" ? "danger" : "secondary";
}

// =============================================================================
// AckStatusPanel — per-PO order acknowledgements for a created order.
// One confirmation per PO ({order}-1, -2, …). Shows send status and offers a
// manual resend (idempotent: an already-Sent ack only re-sends on a fresh
// resend trigger, which these endpoints supply). Auto-refreshes while any ack
// is still Pending/Failed. "Resend all" also covers an order that has only the
// whole-order acknowledgement (no per-PO rows): the API re-queues that one instead
// of answering 404 (Issue 18).
// =============================================================================
// `bare` drops the card chrome so the panel can sit inside the Documents modal
// as a tab body. Default false, so every existing call site is untouched.
function AckStatusPanel({ orderNum, bare = false }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [busy, setBusy]       = useState(null);   // confirmationNumber | "all"
  const [note, setNote]       = useState(null);

  const load = useCallback(async () => {
    if (!orderNum) return;
    setLoading(true); setError(null);
    try {
      const r = await apiFetch(`${API_URL}/api/OrderConfirmation/by-order/${encodeURIComponent(orderNum)}`);
      const d = await r.json().catch(() => null);
      if (!r.ok) throw new Error(d?.message || d?.error || `HTTP ${r.status}`);
      setData(d);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [orderNum]);

  useEffect(() => { load(); }, [load]);

  // Auto-poll while any ack hasn't reached Sent (render/send is async + retried).
  useEffect(() => {
    const confs = data?.confirmations || [];
    // With no per-PO rows the whole-order ack (from the archive) is the one to watch.
    const whole = data?.wholeOrderAck;
    const settled = confs.length > 0 ? confs.every((c) => c.status === "Sent") : !whole || whole.status === "Sent";
    if (settled) return;
    const id = setInterval(load, 6000);
    return () => clearInterval(id);
  }, [data, load]);

  const resend = async (key, url) => {
    setBusy(key); setNote(null); setError(null);
    try {
      const r = await apiFetch(url, { method: "POST" });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d?.message || d?.error || `HTTP ${r.status}`);
      setNote(d?.mode === "whole-order" ? "Whole-order acknowledgement re-queued." : d?.count != null ? `Re-queued ${d.count} acknowledgement(s).` : "Acknowledgement re-queued.");
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const confs = data?.confirmations || [];

  return (
    <div className={bare ? "" : "qw-card-flat qw-pad"} style={bare ? { padding: "14px 18px" } : { marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
        <span className="qw-eyebrow" style={bare ? { display: "none" } : undefined}><FaEnvelope style={{ marginRight: 6 }} /> Acknowledgements{data?.count != null ? ` (${data.count})` : ""}</span>
        {bare && <span style={{ fontSize: 12.5, color: "#475569" }}>Sent to the customer when the order was raised. Resend if they never received it.</span>}
        <div style={{ display: "flex", gap: 6 }}>
          <Button size="sm" variant="outline-secondary" onClick={load} disabled={loading}><FaSync /> Refresh</Button>
          {(confs.length > 0 || data?.wholeOrderAck) && (
            <Button size="sm" variant="outline-primary" disabled={busy === "all"} onClick={() => resend("all", `${API_URL}/api/OrderConfirmation/by-order/${encodeURIComponent(orderNum)}/resend`)}>
              {busy === "all" ? <Spinner animation="border" size="sm" /> : <FaRedo />} Resend all
            </Button>
          )}
        </div>
      </div>

      {note && <Alert variant="success" style={{ fontSize: 12.5, padding: "6px 10px" }} dismissible onClose={() => setNote(null)}>{note}</Alert>}
      {error && <Alert variant="danger" style={{ fontSize: 12.5, padding: "6px 10px" }}>{error}</Alert>}

      {loading && !data ? (
        <div style={{ fontSize: 12.5, color: "#64748B" }}><Spinner animation="border" size="sm" /> Loading acknowledgements…</div>
      ) : confs.length === 0 && data?.wholeOrderAck ? (
        // No per-PO confirmations were planned (held or empty plan), so the customer got the
        // single whole-order acknowledgement instead. The API reports it from the document
        // archive; without this the panel claimed nothing had been sent.
        <div style={{ fontSize: 12.5, color: "#475569", padding: "7px 0", borderTop: "1px solid #f1f5f9" }}>
          <strong>Whole-order acknowledgement</strong>{" "}
          <Badge bg={data.wholeOrderAck.status === "Sent" ? "success" : "danger"} style={{ fontSize: 10.5 }}>{data.wholeOrderAck.status}</Badge>
          <div style={{ color: "#94A3B8", marginTop: 2 }}>
            {data.wholeOrderAck.generatedAt ? new Date(data.wholeOrderAck.generatedAt).toLocaleString() : ""}
            {data.wholeOrderAck.triggerSource ? ` · ${data.wholeOrderAck.triggerSource}` : ""}
            {" · no per-PO confirmations were issued for this order"}
          </div>
        </div>
      ) : confs.length === 0 ? (
        <div style={{ fontSize: 12.5, color: "#94A3B8" }}>No acknowledgements yet — they{"’"}re generated shortly after the order is created.</div>
      ) : (
        confs.map((c) => (
          <div key={c.confirmationNumber} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "7px 0", borderTop: "1px solid #f1f5f9", flexWrap: "wrap" }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span className="qw-mono" style={{ fontWeight: 700, color: "#0F172A", fontSize: 13 }}>{c.confirmationNumber}</span>
                <Badge bg={ackBadge(c.status)}>{c.status}</Badge>
              </div>
              <div style={{ fontSize: 11.5, color: "#94A3B8", marginTop: 2 }}>
                {c.customerPoNum ? `PO ${c.customerPoNum} · ` : ""}{fmtMoney(c.amount)}{c.sentAt ? ` · sent ${fmtWhen(c.sentAt)}` : ""}
              </div>
              {c.lastError && <div style={{ fontSize: 11, color: "#B91C1C", marginTop: 2 }}>{c.lastError}</div>}
            </div>
            <Button size="sm" variant="outline-secondary" disabled={busy === c.confirmationNumber}
              onClick={() => resend(c.confirmationNumber, `${API_URL}/api/OrderConfirmation/${encodeURIComponent(c.confirmationNumber)}/resend`)}>
              {busy === c.confirmationNumber ? <Spinner animation="border" size="sm" /> : <><FaRedo style={{ marginRight: 4 }} /> Resend</>}
            </Button>
          </div>
        ))
      )}
    </div>
  );
}

export default AckStatusPanel;
